import Constants from 'expo-constants';
import { ComponentProps } from 'react';

import { CampaignInfo } from '../api/campaigns';
import BumoInNumbers from '../components/BumoInNumbers';
import { Footer } from '../components/Footer';
import { Container, InnerContainer } from '../components/Layout';
import Loader from '../components/Loader';
import Redirect from '../components/Redirect';
import { TransparentRow } from '../components/Row';
import {
  QuincyRegularText,
  TextH1,
  TextH2,
  TextH3,
} from '../components/StyledText';
import { TransparentView, ViewWithClassname } from '../components/Themed';
import WhyYouNeedBumo from '../components/WhyYouNeedBumo';
import { TopLocations } from '../components/explore/Content';
import { HeroContent } from '../components/explore/HeroWithImage';
import HowItWorks from '../components/explore/HowItWorks';
import Logos from '../components/explore/Logos';
import Testimonials from '../components/explore/Testimonials';
import CommonHeader from '../components/layouts/CommonHeader';
import CTAButton from '../components/lead-pages/CTAButton';
import {
  CampaignInfoProvider,
  useCampaignInfo,
  useCampaignInfoFetching,
} from '../components/lead-pages/CampaignInfoProvider';
import {
  HeartIcon,
  SmallRaysBottom,
  SmallRaysTop,
} from '../components/lead-pages/decorations';
import { SkeletonAtom } from '../components/location_blocks/SkeletonBlockMedium';
import Colors from '../constants/Colors';
import FeatureFlags from '../constants/FeatureFlags';
import { useIsAuthEstablished } from '../contexts/authFlow';
import useColorScheme from '../hooks/useColorScheme';
import { RootStackScreenProps } from '../types';

const leadPagesConfig = {
  acc: {
    headerTag: 'For Altadena Country Club',
    title: 'Instantly bookable care and camps',
    description: ({ campaignInfo }: { campaignInfo: CampaignInfo }) => (
      <>
        Start a free account and get a ${campaignInfo.rewardAmount} voucher you
        can use to book care or camps!
        {campaignInfo.rewardExistingUsers ? '' : '*'}
      </>
    ),
    image: 'hero-image-sfa.webp',
    ctaButtonTitle: (rewardAmount: number) =>
      `Claim $${rewardAmount} voucher for care or camps`,
    cs: {
      uuid: Constants.expoConfig?.extra?.CS_ACC_UUID,
    },
  },
};

function RegisterButton({
  config,
  ...props
}: {
  config: (typeof leadPagesConfig)[keyof typeof leadPagesConfig];
} & ComponentProps<typeof CTAButton>) {
  const campaignInfo = useCampaignInfo();
  const isCampaignInfoFetching = useCampaignInfoFetching();
  const title = campaignInfo
    ? campaignInfo.rewardClaimed
      ? 'Voucher already claimed'
      : config.ctaButtonTitle(campaignInfo.rewardAmount)
    : 'Claim voucher...';
  return (
    <CTAButton
      title={title}
      authenticateParams={{
        source: 'LEAD_PAGE',
        csUuid: config.cs.uuid,
        csReward: campaignInfo?.rewardAmount,
        onlyNewJoiners: !campaignInfo?.rewardExistingUsers,
      }}
      buttonProps={{
        disabled:
          !campaignInfo || campaignInfo.rewardClaimed || isCampaignInfoFetching,
      }}
      {...props}
      csUuid={config.cs.uuid}
    />
  );
}
export default function CampaignLeadPage({
  route,
}: RootStackScreenProps<'CampaignLeadPage'>) {
  const theme = useColorScheme();
  const config =
    leadPagesConfig[route.params.slug as keyof typeof leadPagesConfig];

  if (!FeatureFlags.CAMPAIGN_LEAD_PAGES) {
    return <Redirect to="StartFreeAccount" />;
  }

  if (!config) {
    return <Redirect to="StartFreeAccount" />;
  }
  return (
    <AuthEstablishedLoader>
      <CampaignInfoProvider
        campaignId={config.cs.uuid}
        errorComponent={<Redirect to="StartFreeAccount" />}
      >
        <CommonHeader />
        <Container>
          <InnerContainer>
            <TransparentRow>
              <HeroContent.Text wrapperStyle={{ justifyContent: 'center' }}>
                {config.headerTag && (
                  <TextH3
                    style={{
                      padding: 10,
                      backgroundColor: Colors[theme].accent,
                      alignSelf: 'flex-start',
                      marginBottom: 10,
                    }}
                  >
                    {config.headerTag}
                  </TextH3>
                )}
                <TextH1 style={{ marginBottom: 10 }}>{config.title}</TextH1>
                <CampaignDescription config={config} />

                <RegisterButton
                  buttonColor={Colors[theme].cta}
                  config={config}
                  style={{ alignItems: 'center', alignSelf: 'flex-start' }}
                />
              </HeroContent.Text>
              <HeroContent.Image imageFileName="hero-image-sfa.webp" />
            </TransparentRow>
          </InnerContainer>
          <Logos />
          <InnerContainer style={{ paddingTop: 40 }}>
            <SmallRaysTop />

            <TransparentView
              style={{ alignItems: 'center', paddingVertical: 40 }}
            >
              <TextH2>Why you need Bumo</TextH2>
              <QuincyRegularText
                style={{
                  fontSize: 25,
                  maxWidth: 1000,
                  textAlign: 'center',
                  marginTop: 8,
                }}
              >
                The modern, working parent needs flexible care solutions for
                peace of mind. If you've ever found yourself scrambling for
                care, we see you and we're here for you.
              </QuincyRegularText>

              <TransparentRow
                style={{
                  marginTop: 8,
                }}
              >
                <HeartIcon />
                <HeartIcon />
                <HeartIcon />
              </TransparentRow>
            </TransparentView>

            <WhyYouNeedBumo />
            <RegisterButton config={config} />
            <SmallRaysBottom />
            <TopLocations />
            <BumoInNumbers />
            <HowItWorks style={{ backgroundColor: '#B3D3D64D' }} />
            <TransparentView style={{ paddingVertical: 40 }}>
              <RegisterButton config={config} />
            </TransparentView>
          </InnerContainer>
          <Testimonials
            endAdornment={
              <RegisterButton config={config} buttonColor={Colors[theme].cta} />
            }
          />
          <Footer />
        </Container>
      </CampaignInfoProvider>
    </AuthEstablishedLoader>
  );
}

function AuthEstablishedLoader({ children }: { children: React.ReactNode }) {
  const isAuthEstablished = useIsAuthEstablished();
  if (!isAuthEstablished) {
    return (
      <>
        <CommonHeader />
        <Container>
          <InnerContainer
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              paddingTop: 50,
            }}
          >
            <Loader />
          </InnerContainer>
        </Container>
      </>
    );
  }
  return children;
}

function CampaignDescription({
  config,
}: {
  config: (typeof leadPagesConfig)[keyof typeof leadPagesConfig];
}) {
  const campaignInfo = useCampaignInfo();
  if (!campaignInfo) {
    return (
      <ViewWithClassname
        className="skeleton-container"
        style={{ marginBottom: 25, gap: 10 }}
      >
        <SkeletonAtom style={{ width: '80%', height: 25 }} />
        <SkeletonAtom style={{ width: '80%', height: 25 }} />
        <SkeletonAtom style={{ width: '80%', height: 25 }} />
        <SkeletonAtom style={{ width: 50, height: 25 }} />
      </ViewWithClassname>
    );
  }
  return (
    <QuincyRegularText
      style={{
        fontSize: 25,
        marginBottom: 25,
        maxWidth: 500,
        marginTop: 10,
      }}
    >
      <config.description campaignInfo={campaignInfo} />
    </QuincyRegularText>
  );
}
